import type {
  AccountConnection,
  StartAccountConnectionInput,
  UpstreamAccount,
  UpstreamProvider,
} from '@/services/business/accounts';
import { accountAuthMethods } from '@/services/business/accounts/types';
import { accountStore } from '../store/account';
import { gatewayInstances, gatewayServers } from './fixtures';

export class UpstreamAccountError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}
export const upstreamProviders: UpstreamProvider[] = [
  'codex',
  'grok',
  'gemini',
  'claude',
];
export const upstreamAccounts: UpstreamAccount[] = [];
interface StoredConnection extends AccountConnection {
  ownerId: string;
  accountVersion: number | null;
  detectionFailure: { status: number; message: string } | null;
  credentialIdentity: string | null;
}
const connections = new Map<string, StoredConnection>();
let sequence = 0;
export function resetUpstreamAccounts() {
  const baseline = Date.now();
  const ago = (days: number) => new Date(baseline - days * 86400_000).toISOString();
  upstreamAccounts.splice(
    0,
    upstreamAccounts.length,
    ...(['codex', 'grok', 'gemini', 'claude', 'codex'] as const).map(
      (provider, index): UpstreamAccount => ({
        id: `up-${provider}-${index === 4 ? 'spare' : 'main'}`,
        provider,
        displayName: `${provider === 'codex' ? 'Codex' : provider === 'grok' ? 'Grok' : provider === 'gemini' ? 'Gemini' : 'Claude'} ${index === 4 ? '备用账号' : '主账号'}`,
        maskedIdentity: `${provider[0]}***${index}@example.invalid`,
        authMethod:
          provider === 'codex'
            ? 'official_device'
            : provider === 'claude'
              ? 'official_browser'
              : 'api_key',
        instanceId:
          provider === 'codex' || provider === 'claude' ? 'gateway-sh-01' : null,
        profileRef: null,
        enabled: index !== 2 && index !== 4,
        authStatus:
          index === 1 ? 'expired' : index === 3 ? 'unauthorized' : 'connected',
        authReason:
          index === 1
            ? '登录状态已过期，需要重新授权'
            : index === 3
              ? '账号未授予访问权限'
              : null,
        authorizedAt: index === 3 ? null : ago(index + 1),
        createdAt: ago(30),
        lastUsedAt: index === 3 ? null : ago(index),
        bindings: [
          {
            id: `channel-${provider}-${index === 4 ? 'backup' : 'main'}`,
            name: `${provider} ${index === 4 ? '备用渠道' : '主渠道'}`,
          },
        ],
        quotaRemaining: null,
        version: 1,
      }),
    ),
  );
  connections.clear();
  sequence = 0;
}
resetUpstreamAccounts();
export function findUpstreamAccount(id: string) {
  const account = upstreamAccounts.find((item) => item.id === id);
  if (!account) throw new UpstreamAccountError(404, '上游账号不存在或已移除');
  return account;
}
function ownedConnection(id: string) {
  const session = connections.get(id);
  if (!session || session.ownerId !== accountStore.profile().id)
    throw new UpstreamAccountError(404, '授权会话不存在');
  if (session.status === 'pending' && Date.now() >= Date.parse(session.expiresAt)) {
    session.status = 'expired';
    session.reason = '授权会话已过期，请重新开始';
  }
  return session;
}
function publicConnection({
  ownerId: _ownerId,
  accountVersion: _accountVersion,
  detectionFailure: _detectionFailure,
  credentialIdentity: _credentialIdentity,
  ...session
}: StoredConnection): AccountConnection {
  return session;
}
export function readAccountConnection(id: string) {
  return publicConnection(ownedConnection(id));
}
function requireReadyInstance(instanceId: unknown) {
  if (typeof instanceId !== 'string' || !instanceId)
    throw new UpstreamAccountError(422, '请选择执行实例');
  const instance = gatewayInstances.find((item) => item.id === instanceId);
  if (!instance) throw new UpstreamAccountError(404, '执行实例不存在');
  const server = gatewayServers.find((item) => item.id === instance.serverId);
  if (instance.status !== 'ready' || server?.status !== 'online')
    throw new UpstreamAccountError(409, '执行实例离线或排空中，请选择就绪实例');
  return instance.id;
}
// Non-secret deterministic fixture identity; never used for real credential storage.
function fixtureKeyIdentity(value: string) {
  let hash = 2166136261;
  for (const character of value)
    hash = Math.imul(hash ^ character.charCodeAt(0), 16777619);
  return `key-${(hash >>> 0).toString(16)}`;
}
export function beginAccountConnection(input: Partial<StartAccountConnectionInput>) {
  if (
    !upstreamProviders.includes(input.provider as UpstreamProvider) ||
    typeof input.displayName !== 'string' ||
    !input.displayName.trim() ||
    input.displayName.trim().length > 40 ||
    (input.accountId !== undefined && typeof input.accountId !== 'string')
  )
    throw new UpstreamAccountError(422, '请填写供应商和1–40字的账号名称');
  const account = input.accountId ? findUpstreamAccount(input.accountId) : undefined;
  if (
    !account &&
    upstreamAccounts.some(
      (existing) =>
        existing.provider === input.provider &&
        existing.displayName.toLowerCase() === input.displayName?.trim().toLowerCase(),
    )
  ) {
    throw new UpstreamAccountError(409, '账号名称已存在，请使用其他名称');
  }
  if (account && account.provider !== input.provider)
    throw new UpstreamAccountError(422, '重新授权不能更改供应商');
  const method = input.method;
  if (
    !method ||
    !accountAuthMethods[input.provider as UpstreamProvider].includes(method)
  )
    throw new UpstreamAccountError(422, '供应商不支持此接入方式');
  if (
    account &&
    (account.authMethod !== method ||
      (method !== 'api_key' && account.instanceId !== input.instanceId) ||
      (method === 'server_login' && account.profileRef !== input.profileRef))
  )
    throw new UpstreamAccountError(422, '重新授权不能更改接入方式、执行实例和登录引用');
  const instanceId =
    method === 'api_key' ? null : requireReadyInstance(input.instanceId);
  if (
    method === 'api_key' &&
    (typeof input.apiKey !== 'string' ||
      input.apiKey.trim().length < 12 ||
      input.apiKey.length > 512)
  )
    throw new UpstreamAccountError(422, '请输入12–512位 API Key');
  if (
    method === 'server_login' &&
    (typeof input.profileRef !== 'string' || !/^[\w.-]{1,64}$/.test(input.profileRef))
  )
    throw new UpstreamAccountError(
      422,
      '请输入1–64位 profile 名称，仅支持字母、数字、点、横线和下划线',
    );
  const apiKey = method === 'api_key' ? (input.apiKey?.trim() ?? '') : '';
  const profileRef = method === 'server_login' ? (input.profileRef as string) : null;
  const credentialIdentity =
    method === 'api_key'
      ? fixtureKeyIdentity(apiKey)
      : method === 'server_login'
        ? `${instanceId}/${profileRef}`
        : null;
  const detectionFailure = apiKey.startsWith('invalid')
    ? { status: 422, message: 'API Key 检测失败，请检查后重试' }
    : apiKey.startsWith('denied') || profileRef === 'restricted'
      ? { status: 403, message: '凭据未授予访问权限' }
      : profileRef === 'expired'
        ? { status: 410, message: '已有登录已过期，请在执行实例重新登录' }
        : profileRef && !['primary', 'backup'].includes(profileRef)
          ? { status: 404, message: '执行实例中未找到该 profile，请检查登录引用' }
          : null;
  const session: StoredConnection = {
    id: `account-connection-${++sequence}`,
    provider: input.provider as UpstreamProvider,
    displayName: account?.displayName ?? input.displayName.trim(),
    accountId: account?.id ?? null,
    method,
    instanceId,
    profileRef,
    deviceCode:
      method === 'official_device'
        ? `EXAMPLE-${String(sequence).padStart(4, '0')}`
        : null,
    detectionFailure,
    credentialIdentity,
    status: 'pending',
    reason: null,
    expiresAt: new Date(Date.now() + 5 * 60_000).toISOString(),
    ownerId: accountStore.profile().id,
    accountVersion: account?.version ?? null,
    // Fixed synthetic identities cover normal, expired-session and access-denied paths.
    identities:
      method === 'api_key' || method === 'server_login'
        ? [
            {
              id: 'team',
              label: method === 'api_key' ? 'API Key' : '已有登录',
              maskedIdentity:
                account?.maskedIdentity ??
                `${method === 'api_key' ? 'key' : 'profile'}:${credentialIdentity}`,
            },
          ]
        : [
            {
              id: 'team',
              label: '团队账号',
              maskedIdentity: account?.maskedIdentity ?? 't***@example.invalid',
            },
            ...(account
              ? []
              : [
                  {
                    id: 'team-secondary',
                    label: '团队备用账号',
                    maskedIdentity: 's***@example.invalid',
                  },
                ]),
            {
              id: 'historical',
              label: '历史登录账号',
              maskedIdentity: 'h***@example.invalid',
            },
            {
              id: 'restricted',
              label: '受限账号',
              maskedIdentity: 'r***@example.invalid',
            },
          ],
  };
  connections.set(session.id, session);
  return publicConnection(session);
}
export function finishAccountConnection(id: string, identityId: unknown) {
  const session = ownedConnection(id);
  if (session.status === 'succeeded')
    return findUpstreamAccount(session.accountId as string);
  if (session.status === 'expired')
    throw new UpstreamAccountError(410, session.reason ?? '授权会话已过期');
  if (session.status !== 'pending')
    throw new UpstreamAccountError(409, '授权会话已结束，请重新开始');
  if (session.instanceId) requireReadyInstance(session.instanceId);
  if (session.detectionFailure) {
    session.status = session.detectionFailure.status === 410 ? 'expired' : 'rejected';
    session.reason = session.detectionFailure.message;
    throw new UpstreamAccountError(session.detectionFailure.status, session.reason);
  }
  const identity = session.identities.find((item) => item.id === identityId);
  if (!identity) throw new UpstreamAccountError(422, '请选择账号');
  if (identityId === 'historical' || identityId === 'restricted') {
    session.status = identityId === 'historical' ? 'expired' : 'rejected';
    session.reason =
      identityId === 'historical'
        ? '登录会话已过期，请重新开始'
        : '该账号未授予访问权限，请选择其他账号';
    throw new UpstreamAccountError(
      identityId === 'historical' ? 410 : 403,
      session.reason,
    );
  }
  const timestamp = new Date().toISOString();
  let account: UpstreamAccount;
  if (session.accountId) {
    account = findUpstreamAccount(session.accountId);
    if (account.version !== session.accountVersion) {
      session.status = 'rejected';
      session.reason = '账号已更新，请重新发起授权';
      throw new UpstreamAccountError(409, session.reason);
    }
    account.authStatus = 'connected';
    account.authReason = null;
    account.authorizedAt = timestamp;
    account.version += 1;
  } else {
    if (
      upstreamAccounts.some(
        (a) =>
          a.provider === session.provider &&
          (a.maskedIdentity === identity.maskedIdentity ||
            a.displayName.toLowerCase() === session.displayName.toLowerCase()),
      )
    ) {
      session.status = 'rejected';
      session.reason = '账号或名称已存在，请在列表中查看';
      throw new UpstreamAccountError(409, session.reason);
    }
    account = {
      id: `up-connected-${++sequence}`,
      provider: session.provider,
      displayName: session.displayName,
      maskedIdentity: identity.maskedIdentity,
      authMethod: session.method,
      instanceId: session.instanceId,
      profileRef: session.profileRef,
      enabled: true,
      authStatus: 'connected',
      authReason: null,
      authorizedAt: timestamp,
      createdAt: timestamp,
      lastUsedAt: null,
      bindings: [],
      quotaRemaining: null,
      version: 1,
    };
    upstreamAccounts.unshift(account);
    session.accountId = account.id;
  }
  session.status = 'succeeded';
  return account;
}
export function abortAccountConnection(id: string) {
  const session = ownedConnection(id);
  if (session.status === 'pending') {
    session.status = 'cancelled';
    session.reason = '授权已取消';
  }
  return publicConnection(session);
}
export function changeAccountEnabled(
  id: string,
  input: { enabled?: unknown; expectedVersion?: unknown },
) {
  const account = findUpstreamAccount(id);
  if (
    typeof input.enabled !== 'boolean' ||
    !Number.isInteger(input.expectedVersion) ||
    Number(input.expectedVersion) < 1
  )
    throw new UpstreamAccountError(422, '账号状态参数无效');
  if (input.expectedVersion !== account.version)
    throw new UpstreamAccountError(409, '账号已更新，请刷新后重试');
  account.enabled = input.enabled;
  account.version += 1;
  return account;
}
